import { Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { AuthenticationService } from './../../services/authentication.service';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { LocationService } from '../../services/location.service';
import { FilterSetService, FilterSet } from '../../services/filter-set.service';
import { BbTableService, TableRow } from '../../services/bb-table.service';
import { BbFilterConfiguration, BbFilterPreset } from './bb-filters/bb-filters.component';
import { OverlayService, DialogDefinition } from '../../services/overlay.service';
import { SapiService, SapiEntity } from '../../services/sapi.service';
import { ServiceLocator } from '../../modules/abstract.module';
import { LiveApiService } from './../../services/live-api.service';
import { Directive, OnDestroy, OnInit, TemplateRef } from '@angular/core';

export interface ListConfiguration {
	name: string;
	basePath: string;
	activePath?: string;
	entityTranslation?: string;
	entitiesTranslation?: string;
	tableConfigurable?: boolean;
}

@Directive()
export abstract class AbstractListComponent implements OnInit, OnDestroy {

	filterConfig: BbFilterConfiguration;
	listConfiguration: ListConfiguration;
	rows: any[] = null;
	selectedRows: TableRow[] = [];

	protected getRowsSubscription: Subscription;
	protected offsetChangedSubscription: Subscription;
	protected rowsSelectedSubscription: Subscription;
	protected rowDoubleClickedSubscription: Subscription;
	protected entityTranslation: string;
	protected entitiesTranslation: string;
	protected parsingParams = false;
	protected currentFilterSubscription: Subscription;
	protected customPresets: any[] = [];

	// workaround so extending classes don't have to supply these services to us -- https://stackoverflow.com/a/40592524/4746171
	protected locationService: LocationService;
	protected router: Router;
	protected sapiService: SapiService;
	protected authenticationService: AuthenticationService;
	protected liveApiService: LiveApiService;
	protected translate: TranslateService;
	protected overlayService: OverlayService;
	protected filterSetChangedObservable: Observable<FilterSet>;
	protected filterSetChangedSubscription: Subscription;
	protected currentFilter = 'all';
	protected tableIdentifier: string;
	protected subscriptions = new Subscription();

	constructor(protected bbTableService: BbTableService, protected filterSetService: FilterSetService = null) {
		if (!filterSetService) {
			// no list-specific filtersetService provided, use a generic/shared one
			this.filterSetService = ServiceLocator.injector.get(FilterSetService);
		}

		this.locationService = ServiceLocator.injector.get(LocationService);
		this.router = ServiceLocator.injector.get(Router);
		this.overlayService = ServiceLocator.injector.get(OverlayService);
		this.translate = ServiceLocator.injector.get(TranslateService);
		this.sapiService = ServiceLocator.injector.get(SapiService);
		this.authenticationService = ServiceLocator.injector.get(AuthenticationService);
		this.liveApiService = ServiceLocator.injector.get(LiveApiService);

		if (this.router.url) {
			this.tableIdentifier = this.router.url.split('?')[0].replace(/\//g, '');
			this.bbTableService.setIdentifier(this.tableIdentifier);
		}
	}

	ngOnInit() {
		if (!this.tableIdentifier && this.listConfiguration && this.listConfiguration.name) {
			this.tableIdentifier = this.listConfiguration.name;
			this.bbTableService.setIdentifier(this.tableIdentifier);
		}

		this.filterSetService.setEntity(this.listConfiguration.name);
		this.bbTableService
			.setEntity(this.listConfiguration.name)
			.setConfigurable(this.listConfiguration.tableConfigurable !== undefined ? this.listConfiguration.tableConfigurable : true);
		this.filterSetService.setFilterConfig(this.filterConfig.filters);
		this.filterSetService.setActiveFilter(null);
		this.filterSetChangedObservable = this.filterSetService.filterSetChangedObservable;

		this.currentFilterSubscription = this.filterSetService.currentFilterObservable.subscribe((filter: string | FilterSet) => {
			this.currentFilterChanged(filter);
		});

		this.rowsSelectedSubscription = this.bbTableService.rowsSelectedObservable.subscribe((rows: TableRow[]) => {
			this.rowsSelected(rows);
		});

		this.rowDoubleClickedSubscription = this.bbTableService.rowDoubleClickedObservable.subscribe((row: TableRow) => {
			this.redirectToDetails(row);
		});

		this.offsetChangedSubscription = this.bbTableService.offsetChangedObservable.subscribe(offset => {
			this.selectedRows = []; // remove this when you are going to implement multi-page selections
		});

		this.getRowsSubscription = this.bbTableService.rowsRetrievedObservable.subscribe((rows: TableRow[]) => {
			this.rowsRetrieved(rows);
		});

		this.subscriptions.add(
			this.filterSetService.savePresetObservable.subscribe((customPreset) => {
				this.customPresets.push(customPreset);
				const filters = Object.keys(customPreset.filters).filter((key) => key !== 'sort').map(key => customPreset.filters[key]);

				this.filterConfig.presets.push({
					id: customPreset.id,
					label: customPreset.title,
					value: customPreset.title,
					filterSet: filters
				});
			})
		);

		this.subscriptions.add(
			this.filterSetService.deletePresetObservable.subscribe((id) => {
				const index = this.customPresets.findIndex(customPreset => customPreset.id === id);
				this.customPresets.splice(index, 1);

				const i = this.filterConfig.presets.findIndex(customPreset => customPreset.id === id);
				this.filterConfig.presets.splice(i, 1);
			})
		);

		const defaultTranslationSingle = this.translate.instant(this.listConfiguration.name.toUpperCase()).toLowerCase();
		this.entityTranslation = (this.listConfiguration.entityTranslation || defaultTranslationSingle);

		const defaultTranslationMultiple = this.translate.instant(this.listConfiguration.name + 's'.toUpperCase()).toLowerCase();
		this.entitiesTranslation = (this.listConfiguration.entitiesTranslation || defaultTranslationMultiple);

		this.bbTableService.getRows(true);

	}

	ngOnDestroy() {
		this.getRowsSubscription.unsubscribe();
		this.rowsSelectedSubscription.unsubscribe();
		this.rowDoubleClickedSubscription.unsubscribe();
		this.offsetChangedSubscription.unsubscribe();
		if (this.filterSetChangedSubscription) {
			this.filterSetChangedSubscription.unsubscribe();
		}
		this.currentFilterSubscription.unsubscribe();
		this.subscriptions.unsubscribe();
	}

	rowsRetrieved(rows: TableRow[]) {
		this.rows = rows;
	}

	rowsSelected(rows: TableRow[]) {
		this.selectedRows = rows;
	}

	currentFilterChanged(currentFilter: string | FilterSet) {
		if (typeof currentFilter === 'string') {
			this.currentFilter = currentFilter.toLowerCase();
		}
	}

	redirectToDetails(row: TableRow) {
		if (row && row.data && row.data.id) {
			this.router.navigate([this.listConfiguration.basePath, row.data.id]);
		}
	}

	deleteSelected() {
		if (!this.selectedRows.length) {
			return;
		}

		if (this.selectedRows.length === 1) {
			this.deleteSingle(this.selectedRows[0].data);
			return;
		}

		const dialog: DialogDefinition = {
			icon: 'trash',
			title: this.translate.instant('DELETE_SELECTED_ENTITIES', { entities: this.entitiesTranslation }),
			contents: this.translate.instant('DELETE_SELECTED_ENTITIES_CONFIRM', { entities: this.entitiesTranslation, nr: this.selectedRows.length }),
			onConfirm: () => {

				const ids: string[] = [];

				this.selectedRows.forEach(selectedRow => {
					ids.push(selectedRow.id);
				});

				const observable = this.sapiService.deleteMultiple(this.listConfiguration.name, ids);

				observable.subscribe(() => {
					this.bbTableService.removeRows(ids);

					this.overlayService.createToast(this.translate.instant('SELECTED_ENTITIES_DELETED',
						{ nr: ids.length, entities: this.entitiesTranslation }), 'trash');

				});

				return observable;
			}
		};

		this.overlayService.createDialog(dialog);
	}

	deleteSingle(entity: SapiEntity, dialogContents?: string | TemplateRef<any>) {
		if (!dialogContents) {
			dialogContents = this.translate.instant('DELETE_ENTITY_CONFIRM', { entity: this.entityTranslation });
		}

		const dialog: DialogDefinition = {
			icon: 'trash',
			title: this.translate.instant('DELETING_ENTITY', { entity: this.entityTranslation, id: entity.id }),
			onConfirm: () => {
				let returnObservable: Observable<any>;

				if ((entity as any).sourceid && (entity as any).sourceid.startsWith('live_api_')) {
					const uuid = (entity as any).sourceid.split('live_api_')[1];

					returnObservable = this.liveApiService.deleteUrl(`/stream/${uuid}`).pipe(
						map((data) => {
							this.bbTableService.removeRows([entity.id]);

							this.overlayService.createToast(this.translate.instant('ENTITY_DELETED',
								{ id: entity.id, entity: this.entityTranslation }), 'trash');
						})
					);
				} else {
					returnObservable = this.sapiService.delete(this.listConfiguration.name, entity.id);

					returnObservable.subscribe(() => {
						this.bbTableService.removeRows([entity.id]);

						this.overlayService.createToast(this.translate.instant('ENTITY_DELETED',
							{ id: entity.id, entity: this.entityTranslation }), 'trash');

					});
				}

				return returnObservable;
			}
		};

		if (dialogContents) {
			if (typeof dialogContents !== 'string') {
				dialog.templateRef = dialogContents;
			} else {
				dialog.contents = dialogContents;
			}
		}

		this.overlayService.createDialog(dialog);
	}

	getCustomPresets() {
		this.customPresets = [];
		const params = { sort: 'id asc' };
		this.sapiService.getUrl('/preset', params).subscribe((presets: BbFilterPreset[]) => {
			if (presets) {
				this.customPresets.push(presets);
				for (let i = 0; i < this.customPresets.length; i++) {
					this.customPresets[i]?.items?.forEach((preset: any) => {
						if (preset.filters) {
							const filters = Object.keys(preset.filters).filter((key) => key !== 'sort').map(key => preset.filters[key]);

							this.filterConfig.presets.push({
								id: preset.id,
								label: preset.title,
								value: preset.title,
								filterSet: filters
							});
						}
					});
				}
			}
		});
	}
}
